import { EventBus } from '../core/Events';
import { itemDef, maxStack, type ArmorSlot } from '../data/items';

/** Envanter yuvası. dur = alet/zırh örneğinin kalan dayanıklılığı. */
export interface Slot {
  item: string;
  count: number;
  dur?: number;
  /** Dayanıklılığı sıfırlanan araç saklanır; çalışma masasında onarılabilir. */
  broken?: boolean;
}

export const HOTBAR_SIZE = 8;
export const BACKPACK_SIZE = 32;
export const TOTAL_SLOTS = HOTBAR_SIZE + BACKPACK_SIZE;

export interface InventorySave {
  slots: (Slot | null)[];
  equipment: Partial<Record<ArmorSlot, Slot | null>>;
  selected: number;
}

type InvEvents = {
  changed: void;
  'item:added': { item: string; count: number };
  'item:removed': { item: string; count: number };
  'tool:broken': { item: string };
  'selection:changed': { index: number };
};

/**
 * Envanter.
 * - Yığınlama item tanımından gelir (maxStack)
 * - Her alet/zırh KENDİ dayanıklılığını taşır (yuva bazlı, tip bazlı değil)
 * - İlk 8 yuva hızlı erişim (hotbar)
 */
export class Inventory {
  readonly events = new EventBus<InvEvents>();
  slots: (Slot | null)[] = new Array(TOTAL_SLOTS).fill(null);
  equipment: Record<ArmorSlot, Slot | null> = { head: null, chest: null, legs: null, feet: null };
  private _selected = 0;

  get selected(): number {
    return this._selected;
  }

  select(index: number): void {
    const i = Math.max(0, Math.min(HOTBAR_SIZE - 1, Math.floor(index)));
    if (i === this._selected) return;
    this._selected = i;
    this.events.emit('selection:changed', { index: i });
    this.events.emit('changed', undefined);
  }

  cycle(delta: number): void {
    this.select((this._selected + delta + HOTBAR_SIZE) % HOTBAR_SIZE);
  }

  held(): Slot | null {
    return this.slots[this._selected] ?? null;
  }

  /** Eklenemeyen miktarı döndürür (0 = tamamı sığdı). */
  add(item: string, count = 1, dur?: number): number {
    if (count <= 0) return 0;
    const def = itemDef(item);
    if (def.id === 'unknown') {
      console.warn(`[Inventory] bilinmeyen item eklenemedi: ${item}`);
      return count;
    }
    let left = count;
    const stack = maxStack(item);

    // Araç/zırh yığınlanmaz: her biri ayrı yuvaya, kendi dayanıklılığıyla
    if (stack === 1) {
      while (left > 0) {
        const empty = this.slots.indexOf(null);
        if (empty === -1) break;
        const baseDur = def.tool?.durability ?? def.armor?.durability;
        this.slots[empty] = { item, count: 1, dur: dur ?? baseDur };
        left--;
      }
    } else {
      // Önce mevcut yığınları doldur
      for (let i = 0; i < this.slots.length && left > 0; i++) {
        const s = this.slots[i];
        if (s && s.item === item && s.count < stack) {
          const room = stack - s.count;
          const give = Math.min(room, left);
          s.count += give;
          left -= give;
        }
      }
      // Sonra boş yuvalar
      for (let i = 0; i < this.slots.length && left > 0; i++) {
        if (!this.slots[i]) {
          const give = Math.min(stack, left);
          this.slots[i] = { item, count: give };
          left -= give;
        }
      }
    }
    if (left < count) {
      this.events.emit('item:added', { item, count: count - left });
      this.events.emit('changed', undefined);
    }
    return left;
  }

  count(item: string): number {
    let n = 0;
    for (const s of this.slots) if (s && s.item === item) n += s.count;
    return n;
  }

  has(item: string, count = 1): boolean {
    return this.count(item) >= count;
  }

  /** Belirtilen miktarı siler; yetersizse hiçbir şey silmez ve false döner. */
  remove(item: string, count = 1): boolean {
    if (count <= 0) return true;
    if (!this.has(item, count)) return false;
    let left = count;
    for (let i = this.slots.length - 1; i >= 0 && left > 0; i--) {
      const s = this.slots[i];
      if (!s || s.item !== item) continue;
      const take = Math.min(s.count, left);
      s.count -= take;
      left -= take;
      if (s.count <= 0) this.slots[i] = null;
    }
    this.events.emit('item:removed', { item, count });
    this.events.emit('changed', undefined);
    return true;
  }

  removeAt(index: number, count = 1): Slot | null {
    const s = this.slots[index];
    if (!s) return null;
    const take = Math.min(s.count, count);
    const out: Slot = { item: s.item, count: take, dur: s.dur };
    s.count -= take;
    if (s.count <= 0) this.slots[index] = null;
    this.events.emit('changed', undefined);
    return out;
  }

  /** Elindeki aleti aşındırır. Kırılırsa true döner; alet onarım için yuvada kalır. */
  wearHeld(amount: number): boolean {
    return this.wearAt(this._selected, amount);
  }

  wearAt(index: number, amount: number): boolean {
    const s = this.slots[index];
    if (!s || s.dur === undefined) return false;
    s.dur -= amount;
    if (s.dur <= 0) {
      if (s.broken) return false;
      s.dur = 0;
      s.broken = true;
      this.events.emit('tool:broken', { item: s.item });
      this.events.emit('changed', undefined);
      return true;
    }
    this.events.emit('changed', undefined);
    return false;
  }

  /** Alet/zırhı tam dayanıklılığına çıkarır; yalnız crafting maliyetinden sonra çağrılır. */
  repairAt(index: number): boolean {
    const s = this.slots[index];
    const def = s ? itemDef(s.item) : null;
    const max = def?.tool?.durability ?? def?.armor?.durability;
    if (!s || max === undefined || s.dur === undefined || s.dur >= max) return false;
    s.dur = max;
    delete s.broken;
    this.events.emit('changed', undefined);
    return true;
  }

  /** Zırhı kuşan/çıkar. */
  equip(index: number): boolean {
    const s = this.slots[index];
    const def = s ? itemDef(s.item) : null;
    if (!s || !def?.armor) return false;
    const slot = def.armor.slot;
    const prev = this.equipment[slot];
    this.equipment[slot] = s;
    this.slots[index] = prev ?? null;
    this.events.emit('changed', undefined);
    return true;
  }

  unequip(slot: ArmorSlot): boolean {
    const cur = this.equipment[slot];
    if (!cur) return false;
    const empty = this.slots.indexOf(null);
    if (empty === -1) return false;
    this.slots[empty] = cur;
    this.equipment[slot] = null;
    this.events.emit('changed', undefined);
    return true;
  }

  /** Toplam savunma. */
  defense(): number {
    let d = 0;
    for (const s of Object.values(this.equipment)) {
      if (!s) continue;
      d += itemDef(s.item).armor?.defense ?? 0;
    }
    return d;
  }

  /** Set tamamlandı mı? (Güç Zırhı ÖZ GÜÇ kontrolü) */
  hasFullSet(setId: string): boolean {
    return (['head', 'chest', 'legs', 'feet'] as ArmorSlot[]).every((s) => {
      const cur = this.equipment[s];
      return !!cur && itemDef(cur.item).armor?.set === setId;
    });
  }

  swap(a: number, b: number): void {
    if (a === b) return;
    const A = this.slots[a] ?? null;
    const B = this.slots[b] ?? null;
    // Aynı item ise birleştirmeyi dene
    if (A && B && A.item === B.item && A.dur === undefined && B.dur === undefined) {
      const stack = maxStack(A.item);
      const move = Math.min(stack - B.count, A.count);
      if (move > 0) {
        B.count += move;
        A.count -= move;
        if (A.count <= 0) this.slots[a] = null;
        this.events.emit('changed', undefined);
        return;
      }
    }
    this.slots[a] = B;
    this.slots[b] = A;
    this.events.emit('changed', undefined);
  }

  /** Boş yuva sayısı. */
  freeSlots(): number {
    return this.slots.reduce((n, s) => n + (s ? 0 : 1), 0);
  }

  clear(): void {
    this.slots.fill(null);
    this.equipment = { head: null, chest: null, legs: null, feet: null };
    this.events.emit('changed', undefined);
  }

  toSave(): InventorySave {
    return {
      slots: this.slots.map((s) => (s ? { ...s } : null)),
      equipment: { ...this.equipment },
      selected: this._selected,
    };
  }

  loadSave(data: Partial<InventorySave> | undefined): void {
    this.slots = new Array(TOTAL_SLOTS).fill(null);
    const src = Array.isArray(data?.slots) ? data!.slots! : [];
    for (let i = 0; i < Math.min(TOTAL_SLOTS, src.length); i++) {
      this.slots[i] = sanitizeSlot(src[i]);
    }
    this.equipment = { head: null, chest: null, legs: null, feet: null };
    for (const key of ['head', 'chest', 'legs', 'feet'] as ArmorSlot[]) {
      const s = sanitizeSlot(data?.equipment?.[key] ?? null);
      // Yanlış slota kaydedilmiş zırhı reddet
      if (s && itemDef(s.item).armor?.slot === key) this.equipment[key] = s;
    }
    this._selected = Number.isFinite(data?.selected) ? Math.max(0, Math.min(HOTBAR_SIZE - 1, data!.selected!)) : 0;
    this.events.emit('changed', undefined);
  }
}

/** Bozuk kayıt yuvayı çöpe çevirmesin: doğrula ve kırp. */
function sanitizeSlot(raw: unknown): Slot | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Partial<Slot>;
  if (typeof r.item !== 'string') return null;
  const def = itemDef(r.item);
  if (def.id === 'unknown') return null;
  const count = Number.isFinite(r.count) ? Math.max(1, Math.min(def.stack, Math.floor(r.count as number))) : 1;
  const slot: Slot = { item: def.id, count };
  const baseDur = def.tool?.durability ?? def.armor?.durability;
  if (baseDur !== undefined) {
    const rawDur = Number.isFinite(r.dur) ? Math.max(0, Math.min(baseDur, r.dur as number)) : baseDur;
    slot.dur = rawDur;
    if (rawDur === 0 || r.broken === true) slot.broken = true;
  }
  return slot;
}
